/**
 * Servicio de Notificaciones Push - TorneosPro
 *
 * Gestiona la suscripción a notificaciones y el registro de tokens FCM
 */

import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { collection, addDoc, query, where, getDocs, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@config/firebase';

// Tu VAPID key de Firebase Cloud Messaging (obtener de Firebase Console > Project Settings > Cloud Messaging)
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

class NotificationService {
  constructor() {
    this.messaging = null;
    this.currentToken = null;
    this.onMessageCallback = null;
  }

  /**
   * Inicializar el servicio de mensajería
   */
  async init() {
    if (!('Notification' in window)) {
      console.warn('Este navegador no soporta notificaciones');
      return false;
    }

    if (!VAPID_KEY) {
      console.warn('VAPID key no configurada. Notificaciones push deshabilitadas.');
      return false;
    }

    try {
      const { default: app } = await import('@config/firebase');
      this.messaging = getMessaging(app);
      return true;
    } catch (error) {
      console.error('Error inicializando messaging:', error);
      return false;
    }
  }

  /**
   * Solicitar permiso y obtener token FCM
   */
  async requestPermission(userId) {
    try {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        console.log('Permiso de notificaciones denegado');
        return null;
      }

      if (!this.messaging) {
        const initialized = await this.init();
        if (!initialized) return null;
      }

      // Obtener token FCM
      const token = await getToken(this.messaging, { vapidKey: VAPID_KEY });

      if (token) {
        this.currentToken = token;
        await this.saveToken(userId, token);
        return token;
      }

      return null;
    } catch (error) {
      console.error('Error obteniendo token FCM:', error);
      return null;
    }
  }

  /**
   * Refresca el token cuando el permiso YA está concedido (sin volver a pedir
   * permiso ni mostrar prompt). Se llama al abrir la app: re-guarda el token,
   * lo que de paso limpia duplicados en /fcmTokens. Así el sistema se
   * autorrepara sin depender de ningún botón.
   */
  async refreshToken(userId) {
    try {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        return null;
      }
      if (!this.messaging) {
        const initialized = await this.init();
        if (!initialized) return null;
      }
      const token = await getToken(this.messaging, { vapidKey: VAPID_KEY });
      if (token) {
        this.currentToken = token;
        await this.saveToken(userId, token);
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error refrescando token FCM:', error);
      return null;
    }
  }

  /**
   * Guardar token en Firestore.
   * Política:
   *   - 1 token activo por usuario (al guardar uno nuevo, los viejos del mismo
   *     userId se eliminan).
   *   - 1 doc por token físico: si ese mismo token estaba registrado bajo OTRO
   *     userId (cuenta duplicada, login con otra cuenta en el mismo móvil), se
   *     elimina. Sin esto el mismo dispositivo recibía cada aviso por duplicado.
   */
  async saveToken(userId, token) {
    try {
      const tokensRef = collection(db, 'fcmTokens');

      // 1) Eliminar tokens viejos de este usuario (1 activo por usuario).
      //    Esta query SÍ cumple las reglas (filtra por userId == auth.uid).
      const sameUser = await getDocs(query(tokensRef, where('userId', '==', userId)));
      for (const docSnap of sameUser.docs) {
        await deleteDoc(docSnap.ref);
      }

      // 2) Crear el doc fresco. La limpieza cross-user del MISMO token físico
      //    (otro usuario en el mismo dispositivo) la hace el trigger server-side
      //    onFcmTokenCreated: no se puede hacer desde el cliente porque las reglas
      //    de /fcmTokens solo permiten leer/borrar tus propios docs.
      await addDoc(tokensRef, {
        userId,
        token,
        platform: this.getPlatform(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error guardando token:', error);
      return false;
    }
  }

  /**
   * Eliminar token (logout o desuscripción)
   */
  async removeToken(userId, token) {
    try {
      const tokensRef = collection(db, 'fcmTokens');
      const q = query(tokensRef, where('userId', '==', userId), where('token', '==', token));
      const snapshot = await getDocs(q);

      for (const doc of snapshot.docs) {
        await deleteDoc(doc.ref);
      }

      this.currentToken = null;
      return true;
    } catch (error) {
      console.error('Error eliminando token:', error);
      return false;
    }
  }

  /**
   * Suscribirse a un torneo
   */
  async subscribeToTournament(userId, tournamentId, tournamentName) {
    return this.subscribe(userId, 'tournament', tournamentId, tournamentName);
  }

  /**
   * Suscribirse a un equipo
   */
  async subscribeToTeam(userId, teamId, teamName) {
    return this.subscribe(userId, 'team', teamId, teamName);
  }

  /**
   * Suscripción genérica
   */
  async subscribe(userId, type, entityId, entityName) {
    try {
      // Verificar si ya está suscrito
      const subsRef = collection(db, 'subscriptions');
      const q = query(
        subsRef,
        where('userId', '==', userId),
        where('type', '==', type),
        where('entityId', '==', entityId)
      );
      const existing = await getDocs(q);

      if (!existing.empty) {
        // Ya existe
        return { success: true, alreadySubscribed: true };
      }

      // Crear suscripción
      await addDoc(subsRef, {
        userId,
        type,
        entityId,
        entityName,
        notifications: {
          matchReminder: true,    // 1h antes del partido
          matchResult: true,      // Resultado final
          standings: false,       // Cambios en clasificación
          news: true              // Noticias del torneo
        },
        createdAt: serverTimestamp()
      });

      return { success: true, alreadySubscribed: false };
    } catch (error) {
      console.error('Error suscribiendo:', error);
      return { success: false, error };
    }
  }

  /**
   * Cancelar suscripción
   */
  async unsubscribe(userId, type, entityId) {
    try {
      const subsRef = collection(db, 'subscriptions');
      const q = query(
        subsRef,
        where('userId', '==', userId),
        where('type', '==', type),
        where('entityId', '==', entityId)
      );
      const snapshot = await getDocs(q);

      for (const doc of snapshot.docs) {
        await deleteDoc(doc.ref);
      }

      return { success: true };
    } catch (error) {
      console.error('Error cancelando suscripción:', error);
      return { success: false, error };
    }
  }

  /**
   * Obtener suscripciones del usuario
   */
  async getUserSubscriptions(userId) {
    try {
      const subsRef = collection(db, 'subscriptions');
      const q = query(subsRef, where('userId', '==', userId));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error obteniendo suscripciones:', error);
      return [];
    }
  }

  /**
   * Verificar si está suscrito
   */
  async isSubscribed(userId, type, entityId) {
    try {
      const subsRef = collection(db, 'subscriptions');
      const q = query(
        subsRef,
        where('userId', '==', userId),
        where('type', '==', type),
        where('entityId', '==', entityId)
      );
      const snapshot = await getDocs(q);

      return !snapshot.empty;
    } catch (error) {
      return false;
    }
  }

  /**
   * Escuchar mensajes en primer plano
   */
  onForegroundMessage(callback) {
    if (!this.messaging) return;

    this.onMessageCallback = callback;
    onMessage(this.messaging, (payload) => {
      console.log('Mensaje recibido en primer plano:', payload);

      if (callback) {
        // Los mensajes son solo-data: título y cuerpo vienen en payload.data.
        const data = payload.data || {};
        callback({
          title: data.title || payload.notification?.title || 'TorneosPro',
          body: data.body || payload.notification?.body || '',
          data
        });
      }
    });
  }

  /**
   * Detectar plataforma
   */
  getPlatform() {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/android/.test(userAgent)) return 'android';
    if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
    if (/windows/.test(userAgent)) return 'windows';
    if (/macintosh/.test(userAgent)) return 'mac';
    return 'web';
  }

  /**
   * Verificar si las notificaciones están habilitadas
   */
  isEnabled() {
    return 'Notification' in window && Notification.permission === 'granted';
  }

  /**
   * Obtener estado del permiso
   */
  getPermissionState() {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission; // 'granted', 'denied', 'default'
  }

  /**
   * Token FCM actual de este dispositivo (si se ha registrado en esta sesión).
   */
  getCurrentToken() {
    return this.currentToken;
  }
}

// Exportar instancia singleton
export const notificationService = new NotificationService();
export default notificationService;
